import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ModelCost, Model, Firm, CostItem, Workshop, CalculationType } from "../types";
import {
  costService,
  modelService,
  firmService,
  workshopService,
} from "../services/dataService";
import {
  formatCurrency,
  formatNumber,
  turkishIncludes,
} from "../utils/formatters";
import { useExchangeRates } from "../context/ExchangeRateContext";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import PageLoader from "../components/PageLoader";
import { firmHue, firmInitials } from "../components/FirmPicker";
import "./PurchaseOrders.css";
import "./CostManagement.css";
import { usePermissions } from "../context/PermissionContext";

/**
 * Model maliyetleri — firma listesi → firmanın modelleri → modelin sipariş bazlı maliyet setleri.
 * Ortak po-* dili (PurchaseOrders.css) + yalnız bu ekrana özgü mc-* ekleri (CostManagement.css).
 * Gömülü kullanım (ModelCostsModal): başlık/kırıntı çizilmez, yalnız setler gösterilir.
 */

interface ModelCostsProps {
  /** Verilirse sayfa rota yerine bu modeli gösterir (modal içinde gömülü kullanım). */
  modelId?: string;
  onClose?: () => void;
}

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
  rate: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  shirt: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3 3 6l2 5 3-1v11h8V10l3 1 2-5-5-3a4 4 0 0 1-8 0Z" /></svg>,
};

/** "-" gibi yer tutucu adları boş say (bazı modeller adsız "-" ile açılmış). */
const meaningful = (s?: string | null) => {
  const t = (s || "").trim();
  return t && !/^[-–—_.\s]+$/.test(t) ? t : "";
};
const modelLabel = (name?: string | null, code?: string | null) => meaningful(name) || meaningful(code);
const fmtLongDate = (s: string) => new Date(s).toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" });

/** Esc içteki modalı kapatsın, gömülü kullanımda dıştaki modele ulaşmasın (capture + stopPropagation). */
const useEscClose = (onClose: () => void) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);
};

const ModelCosts: React.FC<ModelCostsProps> = (props) => {
  const { has } = usePermissions(); // yetki matrisi
  const params = useParams<{ modelId?: string; firmId?: string; orderId?: string }>();
  const embedded = !!props.modelId;
  const modelId = props.modelId ?? params.modelId;
  const firmId = embedded ? undefined : params.firmId;
  const orderId = embedded ? undefined : params.orderId;
  // Gömülü kullanımda her sipariş setine kalem eklenebilir; hedef sipariş burada tutulur
  const [addOrderId, setAddOrderId] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { usdRate, eurRate, gbpRate } = useExchangeRates();

  // State
  const [firms, setFirms] = useState<Firm[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [model, setModel] = useState<Model | null>(null);
  const [modelCosts, setModelCosts] = useState<ModelCost[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ModelCost | null>(null);
  const [firmSearchTerm, setFirmSearchTerm] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    if (modelId) {
      // Model detayı görünümü
      loadModel();
      loadModelCosts();
      loadCostItems();
      if (orderId || embedded) {
        loadWorkshops();
      }
    } else if (firmId) {
      // Firma modelleri görünümü
      loadFirmModels();
    } else {
      // Firma listesi görünümü
      loadFirms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, firmId, orderId]);

  const loadCostItems = async () => {
    try {
      const data = await costService.getCostItems();
      setCostItems(data);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
    }
  };

  const loadWorkshops = async () => {
    try {
      const data = await workshopService.getAll();
      setWorkshops(data);
    } catch (error) {
      console.error("❌ Failed to load workshops:", error);
    }
  };

  const loadFirms = async () => {
    try {
      setLoading(true);
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("❌ Failed to load firms:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFirmModels = async () => {
    if (!firmId) return;
    try {
      setLoading(true);
      const [allModels, allFirms] = await Promise.all([
        modelService.getAll(),
        firmService.getFirms(),
      ]);
      const firmModels = allModels.filter((m) => m.firmId === firmId);
      setModels(firmModels);
      setFirms(allFirms);
    } catch (error) {
      console.error("❌ Failed to load firm models:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadModel = async () => {
    if (!modelId) return;
    try {
      const [allModels, allFirms] = await Promise.all([
        modelService.getAll(),
        firmService.getFirms(),
      ]);
      const found = allModels.find((m) => m.modelId === modelId);
      setModel(found || null);
      setFirms(allFirms);
    } catch (error) {
      console.error("❌ Failed to load model:", error);
    }
  };

  const loadModelCosts = async () => {
    if (!modelId) return;
    try {
      setLoading(true);
      // orderId varsa siparişe göre, yoksa modele göre (siparişler arası tüm maliyetler)
      let data = orderId
        ? await costService.getModelCosts(orderId)
        : await costService.getModelCostsByModel(modelId!);

      setModelCosts(data);

      // Başlangıçta tüm order'lar kapalı olsun
      setExpandedOrders(new Set());
    } catch (error: any) {
      console.error("❌ Failed to load model costs:", error);
      setModelCosts([]);
      // CORS veya network hatası için daha açıklayıcı mesaj
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("CORS")
      ) {
        console.warn(
          "⚠️ Model maliyetleri yüklenemedi (Bağlantı hatası). Boş liste gösteriliyor.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: ModelCost) => {
    setConfirmModal({
      isOpen: true,
      message: `"${item.costItemName || "Bu"}" maliyet kalemini bu modelden kaldırmak istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteModelCost(item.modelCostId);
          setAlertModal({
            isOpen: true,
            message: "Model maliyeti başarıyla kaldırıldı!",
            type: "success",
          });
          loadModelCosts();
        } catch (error: any) {
          console.error("❌ Failed to delete model cost:", error);
          setAlertModal({
            isOpen: true,
            message: "Model maliyeti kaldırılamadı!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleEdit = (item: ModelCost) => {
    // CostItem'dan quantity label'ları al
    const costItem = costItems.find((ci) => ci.costItemId === item.costItemId);
    const enrichedItem = {
      ...item,
      quantity1Label: item.quantity1Label || costItem?.quantity1Label,
      quantity2Label: item.quantity2Label || costItem?.quantity2Label,
    };
    setEditingItem(enrichedItem);
    setShowEditModal(true);
  };

  const handleUpdateModelCost = async (updatedItem: ModelCost) => {
    try {
      setLoading(true);
      await costService.updateModelCost(updatedItem.modelCostId, updatedItem);
      setAlertModal({
        isOpen: true,
        message: "Model maliyeti başarıyla güncellendi!",
        type: "success",
      });
      setShowEditModal(false);
      setEditingItem(null);
      loadModelCosts();
    } catch (error: any) {
      console.error("❌ Failed to update model cost:", error);
      setAlertModal({
        isOpen: true,
        message: "Model maliyeti güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddModelCost = async (newCost: Partial<ModelCost>) => {
    const targetOrderId = orderId || addOrderId;
    if (!modelId || !targetOrderId) {
      setAlertModal({
        isOpen: true,
        message: "Model ve sipariş bilgisi gereklidir!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const modelCostData = {
        ...newCost,
        modelId,
        orderId: targetOrderId,
        usdRate: usdRate || undefined,
        eurRate: eurRate || undefined,
        gbpRate: gbpRate || undefined,
        exchangeRateDate: new Date().toISOString(),
        isActive: true,
      };

      await costService.addModelCost(
        modelCostData as Omit<ModelCost, "modelCostId">,
      );
      setAlertModal({
        isOpen: true,
        message: "Maliyet başarıyla eklendi.",
        type: "success",
      });
      setShowAddModal(false);
      loadModelCosts();
    } catch (error: any) {
      console.error("❌ Failed to add model cost:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Maliyet eklenemedi: " + (error.message || "Bilinmeyen hata"),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Döviz dönüşümü fonksiyonu
  const convertToTRY = (
    amount: number,
    currency: string,
    exchangeRates: { usdRate?: number; eurRate?: number; gbpRate?: number },
  ): number => {
    if (currency === "TRY") return amount;

    const { usdRate, eurRate, gbpRate } = exchangeRates;

    switch (currency) {
      case "USD":
        if (!usdRate) {
          console.warn("⚠️ USD kuru bulunamadı, dönüşüm yapılamıyor");
          return 0;
        }
        return amount * usdRate;
      case "EUR":
        if (!eurRate) {
          console.warn("⚠️ EUR kuru bulunamadı, dönüşüm yapılamıyor");
          return 0;
        }
        return amount * eurRate;
      case "GBP":
        if (!gbpRate) {
          console.warn("⚠️ GBP kuru bulunamadı, dönüşüm yapılamıyor");
          return 0;
        }
        return amount * gbpRate;
      default:
        return amount;
    }
  };

  // Order'lara göre grupla
  const groupByOrder = () => {
    const grouped = new Map<string, ModelCost[]>();

    modelCosts.forEach((cost) => {
      const orderKey = cost.orderId || "general";
      if (!grouped.has(orderKey)) {
        grouped.set(orderKey, []);
      }
      grouped.get(orderKey)!.push(cost);
    });

    return grouped;
  };

  // Her grup için toplam maliyeti TL'ye çevirerek hesapla
  const calculateGroupTotal = (costs: ModelCost[]): number => {
    return costs.reduce((sum, item) => {
      const itemTotal = item.totalCost || 0;
      const tryAmount = convertToTRY(itemTotal, item.currency || "TRY", {
        usdRate: item.usdRate,
        eurRate: item.eurRate,
        gbpRate: item.gbpRate,
      });
      return sum + tryAmount;
    }, 0);
  };

  const toggleOrderExpanded = (orderKey: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderKey)) {
      newExpanded.delete(orderKey);
    } else {
      newExpanded.add(orderKey);
    }
    setExpandedOrders(newExpanded);
  };

  const toggleAllOrders = () => {
    const allOrderKeys = Array.from(groupByOrder().keys());
    if (expandedOrders.size === allOrderKeys.length) {
      // Tümü açık, hepsini kapat
      setExpandedOrders(new Set());
    } else {
      // Tümünü aç
      setExpandedOrders(new Set(allOrderKeys));
    }
  };

  const crumbs = (items: { label: string; to?: string }[]) => (
    <nav className="mc-crumbs" aria-label="Konum">
      {items.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mc-crumb-sep" aria-hidden="true">/</span>}
          {c.to ? (
            <button type="button" className="po-link" onClick={() => navigate(c.to!)}>
              {i === 0 && <span className="mc-crumb-back" aria-hidden="true">{I.back}</span>}
              {c.label}
            </button>
          ) : (
            <span className="mc-crumb-here" aria-current="page">{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );

  // ============================================
  // RENDER: Firma Listesi Görünümü
  // ============================================
  if (!firmId && !modelId) {
    if (loading) {
      return <PageLoader message="Firmalar yükleniyor..." />;
    }

    const filteredFirms = firms.filter(
      (firm) =>
        turkishIncludes(firm.firmName, firmSearchTerm) ||
        turkishIncludes(firm.firmCode, firmSearchTerm) ||
        turkishIncludes(firm.contactPerson || "", firmSearchTerm) ||
        turkishIncludes(firm.phone || "", firmSearchTerm) ||
        turkishIncludes(firm.email || "", firmSearchTerm),
    );

    return (
      <div className="po-page mc-page">
        <header className="po-head">
          <div>
            <span className="po-kicker">Maliyet</span>
            <h1>Model maliyetleri</h1>
            <p>Firma seçin, ardından modeli; her modelin sipariş bazlı maliyet setleri ve TL karşılıkları açılır.</p>
          </div>
        </header>

        <section className="po-card">
          <div className="po-toolbar">
            <div className="po-search">
              {I.search}
              <input
                placeholder="Firma ara (kod, ad, yetkili, telefon, e-posta)"
                value={firmSearchTerm}
                onChange={(e) => setFirmSearchTerm(e.target.value)}
              />
              {firmSearchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setFirmSearchTerm("")}>{I.close}</button>}
            </div>
          </div>
          <div className="po-card-head">
            <span><b>{filteredFirms.length}</b> firma{filteredFirms.length !== firms.length && <> / {firms.length}</>}</span>
            <span className="po-hint po-hide-sm">Satıra tıklayınca firmanın modelleri açılır</span>
          </div>
          {filteredFirms.length === 0 ? (
            <div className="po-blank">
              <div className="po-blank-icon">{I.inbox}</div>
              <b>{firms.length === 0 ? "Henüz firma yok" : "Aramayla eşleşen firma yok"}</b>
              {firms.length === 0 ? "Firmalar ekranından firma tanımlandığında burada listelenir." : "Aramayı değiştirin ya da temizleyin."}
            </div>
          ) : (
            <div className="po-table-wrap">
              <table className="po-table">
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th className="po-hide-sm">İletişim</th>
                    <th className="act" aria-label="Aç" />
                  </tr>
                </thead>
                <tbody>
                  {filteredFirms.map((firm) => (
                    <tr key={firm.firmId} onClick={() => navigate(`/model-costs/firm/${firm.firmId}`)}>
                      <td>
                        <div className="po-row">
                          <span className="po-mark" style={{ ["--po-hue" as any]: firmHue(firm.firmCode || firm.firmName) }}>{firmInitials(firm.firmName)}</span>
                          <div>
                            <div className="po-name">{firm.firmName}</div>
                            <div className="po-sub">{firm.firmCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="po-hide-sm">
                        {firm.contactPerson && <div>{firm.contactPerson}</div>}
                        <div className={firm.contactPerson ? "po-sub" : ""}>{firm.phone || firm.email || "—"}</div>
                      </td>
                      <td className="act">
                        <button
                          type="button"
                          className="po-btn small"
                          onClick={(e) => { e.stopPropagation(); navigate(`/model-costs/firm/${firm.firmId}`); }}
                        >
                          Modeller {I.chev}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  // ============================================
  // RENDER: Firma Modelleri Görünümü
  // ============================================
  if (firmId && !modelId) {
    if (loading) {
      return <PageLoader message="Modeller yükleniyor..." />;
    }

    const currentFirm = firms.find((f) => f.firmId === firmId);
    const filteredModels = models.filter(
      (model) =>
        turkishIncludes(model.modelName, modelSearchTerm) ||
        turkishIncludes(model.modelCode, modelSearchTerm) ||
        turkishIncludes(model.category || "", modelSearchTerm) ||
        turkishIncludes(model.season || "", modelSearchTerm),
    );

    return (
      <div className="po-page mc-page">
        {crumbs([{ label: "Firmalar", to: "/model-costs" }, { label: currentFirm?.firmName || "Firma" }])}
        <header className="po-head">
          <div>
            <span className="po-kicker">Maliyet · Firma{currentFirm?.firmCode ? ` · ${currentFirm.firmCode}` : ""}</span>
            <h1>{currentFirm?.firmName || "Firma"}</h1>
            <p>{models.length} model. Model seçerek sipariş bazlı maliyet setlerine ulaşın.</p>
          </div>
        </header>

        <section className="po-card">
          <div className="po-toolbar">
            <div className="po-search">
              {I.search}
              <input
                placeholder="Model ara (kod, ad, kategori, sezon)"
                value={modelSearchTerm}
                onChange={(e) => setModelSearchTerm(e.target.value)}
              />
              {modelSearchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setModelSearchTerm("")}>{I.close}</button>}
            </div>
          </div>
          <div className="po-card-head">
            <span><b>{filteredModels.length}</b> model{filteredModels.length !== models.length && <> / {models.length}</>}</span>
            <span className="po-hint po-hide-sm">Satıra tıklayınca maliyet setleri açılır</span>
          </div>
          {filteredModels.length === 0 ? (
            <div className="po-blank">
              <div className="po-blank-icon">{I.shirt}</div>
              <b>{models.length === 0 ? "Bu firmaya ait model yok" : "Aramayla eşleşen model yok"}</b>
              {models.length === 0 ? "Modeller ekranından bu firmaya model eklendiğinde burada listelenir." : "Aramayı değiştirin ya da temizleyin."}
            </div>
          ) : (
            <div className="po-table-wrap">
              <table className="po-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th className="po-hide-sm">Kategori</th>
                    <th className="po-hide-sm">Sezon</th>
                    <th className="act" aria-label="Aç" />
                  </tr>
                </thead>
                <tbody>
                  {filteredModels.map((m) => {
                    const name = meaningful(m.modelName);
                    const code = meaningful(m.modelCode);
                    return (
                      <tr key={m.modelId} onClick={() => navigate(`/model-costs/model/${m.modelId}`)}>
                        <td>
                          <div className={`po-name ${name ? "" : "mc-unnamed"}`}>{name || "Adsız model"}</div>
                          <div className="po-sub">
                            {code ? <span className="po-code">{code}</span> : "kod yok"}
                            <span className="po-only-sm">{[m.category, m.season].filter(Boolean).map((x) => ` · ${x}`).join("")}</span>
                          </div>
                        </td>
                        <td className="po-hide-sm">{m.category || "—"}</td>
                        <td className="po-hide-sm">{m.season || "—"}</td>
                        <td className="act">
                          <button
                            type="button"
                            className="po-btn small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/model-costs/model/${m.modelId}`); }}
                          >
                            Maliyetler {I.chev}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  // ============================================
  // RENDER: Model Detayı ve Maliyetler
  // ============================================
  if (loading && modelCosts.length === 0) {
    return embedded
      ? <div className="po-page mc-page embedded"><div className="po-loading"><span className="po-spin" /> Maliyetler yükleniyor</div></div>
      : <PageLoader message="Model maliyetleri yükleniyor..." />;
  }

  // Başlık: model adı yer tutucuysa ("-") koda, o da yoksa maliyet satırındaki ada düşer
  const firstRow = modelCosts[0];
  const modelTitle =
    modelLabel(model?.modelName, model?.modelCode) ||
    modelLabel(firstRow?.modelName, firstRow?.modelCode) ||
    (model || firstRow ? "Adsız model" : "Model");
  const modelCode = meaningful(model?.modelCode || firstRow?.modelCode);
  const modelFirmId = model?.firmId || firstRow?.firmId;
  const modelFirmName = firms.find((f) => f.firmId === modelFirmId)?.firmName || firstRow?.firmName || "";

  const groups = Array.from(groupByOrder());
  const groupStats = groups.map(([orderKey, costs]) => {
    const groupTotal = calculateGroupTotal(costs);
    const orderQuantity = costs[0]?.orderQuantity || 0;
    return { orderKey, costs, groupTotal, orderQuantity, unitCost: orderQuantity > 0 ? groupTotal / orderQuantity : 0 };
  });
  const latest = groupStats[0];
  const unitSets = groupStats.filter((g) => g.orderQuantity > 0);
  const avgUnit = unitSets.length ? unitSets.reduce((s, g) => s + g.unitCost, 0) / unitSets.length : 0;
  const allOpen = groups.length > 0 && expandedOrders.size === groups.length;

  const head = !embedded && (
    <>
      {crumbs([
        { label: "Firmalar", to: "/model-costs" },
        ...(modelFirmId ? [{ label: modelFirmName || "Firma", to: `/model-costs/firm/${modelFirmId}` }] : []),
        { label: modelTitle },
      ])}
      <header className="po-head">
        <div>
          <span className="po-kicker">Maliyet · Model{orderId ? " · Tek sipariş" : ""}</span>
          <h1>
            {modelTitle}
            {modelCode && modelCode !== modelTitle && <span className="po-codepill">{modelCode}</span>}
          </h1>
          <p>
            {modelFirmName && <>{modelFirmName}. </>}
            Sipariş bazlı maliyet setleri; dövizli kalemler kayıt anındaki kurla TL'ye çevrilir.
          </p>
        </div>
        {groups.length > 1 && (
          <div className="po-head-actions">
            <button type="button" className="po-btn" onClick={toggleAllOrders} aria-pressed={allOpen}>
              <span className={`mc-chev ${allOpen ? "open" : ""}`}>{I.chev}</span>
              {allOpen ? "Tümünü kapat" : "Tümünü aç"}
            </button>
          </div>
        )}
      </header>
    </>
  );

  if (!loading && modelCosts.length === 0 && modelId) {
    return (
      <div className={`po-page mc-page ${embedded ? "embedded" : ""}`}>
        {head}
        <section className="po-card">
          <div className="po-blank">
            <div className="po-blank-icon">{I.inbox}</div>
            <b>Bu model için henüz maliyet kaydı yok</b>
            Sipariş oluşturulduğunda maliyetler otomatik olarak burada görünür.
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`po-page mc-page ${embedded ? "embedded" : ""}`}>
      {head}

      {!embedded && latest && (
        <section className="po-kpis">
          <div className="po-kpi navy">
            <span className="l">Maliyet seti</span>
            <span className="v">{groups.length}</span>
            <span className="s">{groups.length === 1 ? "tek sipariş" : "sipariş bazında"}</span>
          </div>
          <div className="po-kpi">
            <span className="l">Maliyet kalemi</span>
            <span className="v">{modelCosts.length}</span>
            <span className="s">tüm setlerde toplam satır</span>
          </div>
          <div className="po-kpi">
            <span className="l">{groups.length > 1 ? "Son set toplamı" : "Set toplamı"}</span>
            <span className="v">{formatCurrency(latest.groupTotal, "TRY")}</span>
            <span className="s">{latest.orderQuantity > 0 ? <><b>{formatCurrency(latest.unitCost, "TRY")}</b> birim · {latest.orderQuantity} adet</> : "adet bilgisi yok"}</span>
          </div>
          <div className="po-kpi ok">
            <span className="l">Ortalama birim maliyet</span>
            <span className="v">{unitSets.length ? formatCurrency(avgUnit, "TRY") : "—"}</span>
            <span className="s">{unitSets.length ? `${unitSets.length} setin ortalaması` : "adetli set yok"}</span>
          </div>
        </section>
      )}

      {embedded && groups.length > 1 && (
        <div className="mc-embedded-bar">
          <span className="po-hint"><b>{groups.length}</b> maliyet seti · <b>{modelCosts.length}</b> kalem</span>
          <button type="button" className="po-btn small" onClick={toggleAllOrders} aria-pressed={allOpen}>
            <span className={`mc-chev ${allOpen ? "open" : ""}`}>{I.chev}</span>
            {allOpen ? "Tümünü kapat" : "Tümünü aç"}
          </button>
        </div>
      )}

      {/* Order Bazlı Gruplama */}
      <div className="mc-sets">
        {groupStats.map(({ orderKey, costs, groupTotal, orderQuantity, unitCost }) => {
          const firstCost = costs[0];
          const open = expandedOrders.has(orderKey);
          const general = orderKey === "general";
          const canAdd = !general && (orderId || embedded) && has("model-costs:create");

          return (
            <section key={orderKey} className={`po-card mc-set ${open ? "open" : ""} ${loading ? "dim" : ""}`}>
              {/* Grup başlığı — tıklayınca açılır/kapanır */}
              <div className="mc-set-head" onClick={() => toggleOrderExpanded(orderKey)}>
                <button type="button" className="mc-set-toggle" aria-expanded={open} aria-label={open ? "Seti kapat" : "Seti aç"}>
                  <span className={`mc-chev ${open ? "open" : ""}`}>{I.chev}</span>
                </button>
                <div className="mc-set-main">
                  <div className="mc-set-title">
                    <b>{general ? "Genel model maliyetleri" : "Sipariş"}</b>
                    {!general && <span className="po-codepill" title={orderKey}>#{orderKey.slice(0, 8).toUpperCase()}</span>}
                    {orderQuantity > 0 && <span className="po-badge info">{orderQuantity} adet</span>}
                  </div>
                  <div className="mc-set-meta">
                    {firstCost?.exchangeRateDate && (
                      <span>{I.cal}{fmtLongDate(firstCost.exchangeRateDate)}</span>
                    )}
                    <span>
                      {I.rate}
                      Kurlar{firstCost?.createdAt && ` (${new Date(firstCost.createdAt).toLocaleDateString("tr-TR")})`}:
                      {" "}USD <b>{firstCost?.usdRate?.toFixed(4) || "yok"}</b>
                      {" · "}EUR <b>{firstCost?.eurRate?.toFixed(4) || "yok"}</b>
                    </span>
                  </div>
                </div>
                <div className="mc-set-figs">
                  <div><span>Kalem</span><b>{costs.length}</b></div>
                  {orderQuantity > 0 && <div><span>Birim</span><b>{formatCurrency(unitCost, "TRY")}</b></div>}
                  <div className="grand"><span>Toplam</span><b>{formatCurrency(groupTotal, "TRY")}</b></div>
                </div>
                {/* Yeni maliyet — yalnız sipariş setinde ve yetki varsa */}
                {canAdd && (
                  <button
                    type="button"
                    className="po-btn small mc-set-add"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddOrderId(orderKey);
                      setShowAddModal(true);
                    }}
                  >
                    {I.plus} Kalem ekle
                  </button>
                )}
              </div>

              {/* Detaylar - Açıldığında Göster */}
              {open && (
                <div className="po-table-wrap mc-set-body">
                  <table className="po-table small mc-cost-table">
                    <thead>
                      <tr>
                        <th>Maliyet kalemi</th>
                        <th className="num">Miktar 1</th>
                        <th className="num">Miktar 2</th>
                        <th className="num">3. boyut (ref)</th>
                        <th className="num">Fire</th>
                        <th className="num">Net miktar</th>
                        <th className="num">Birim fiyat</th>
                        <th className="num">Toplam</th>
                        <th className="num">TL karşılığı</th>
                        <th>Kullanım</th>
                        {(has("model-costs:update") || has("model-costs:delete")) && <th className="act" aria-label="İşlemler" />}
                      </tr>
                    </thead>
                    <tbody>
                      {costs.map((item) => {
                        const tryEquivalent = convertToTRY(
                          item.totalCost || 0,
                          item.currency || "TRY",
                          {
                            usdRate: item.usdRate,
                            eurRate: item.eurRate,
                            gbpRate: item.gbpRate,
                          },
                        );

                        return (
                          <tr key={item.modelCostId} className="static">
                            <td>
                              <div className="po-name">{item.costItemName || "—"}</div>
                              <div className="po-sub">{item.costCategoryName || "kategori yok"}</div>
                            </td>
                            <td className="num">
                              <b>{item.quantity}</b> <span className="po-sub">{item.unit || ""}</span>
                            </td>
                            <td className="num">
                              {item.quantity2 ? <><b>{item.quantity2}</b> <span className="po-sub">{item.unit2 || ""}</span></> : <span className="mc-nil">—</span>}
                            </td>
                            <td className="num">
                              {item.quantity3 && item.unit3 ? <span className="mc-ref"><b>{item.quantity3}</b> × {item.unit3}</span> : <span className="mc-nil">—</span>}
                            </td>
                            <td className="num">
                              {item.wastagePercentage !== undefined && item.wastagePercentage !== null
                                ? <span className={item.wastagePercentage > 0 ? "mc-waste" : "po-sub"}>%{item.wastagePercentage}</span>
                                : <span className="mc-nil">—</span>}
                            </td>
                            <td className="num">
                              {item.actualQuantityNeeded ? <b>{formatNumber(item.actualQuantityNeeded)}</b> : item.quantity}
                            </td>
                            <td className="num">
                              {item.unitPrice ? formatCurrency(item.unitPrice, item.currency) : <span className="mc-nil">—</span>}
                            </td>
                            <td className="num">
                              <span className="po-amt">{item.totalCost ? formatCurrency(item.totalCost, item.currency) : "—"}</span>
                            </td>
                            <td className="num">
                              <span className="po-amt mc-try">{formatCurrency(tryEquivalent, "TRY")}</span>
                              {item.currency !== "TRY" && item.usdRate && (
                                <div className="po-sub">
                                  1 {item.currency} ={" "}
                                  {item.currency === "USD"
                                    ? item.usdRate
                                    : item.currency === "EUR"
                                      ? item.eurRate
                                      : item.gbpRate}{" "}
                                  TL
                                </div>
                              )}
                            </td>
                            <td>
                              {item.order?.workshop?.name ? (
                                <span className="po-badge muted">{item.order.workshop.name}</span>
                              ) : item.usage ? (
                                <span className="mc-usage" title={item.usage}>
                                  {item.usage.substring(0, 20)}
                                  {item.usage.length > 20 ? "…" : ""}
                                </span>
                              ) : (
                                <span className="mc-nil">—</span>
                              )}
                            </td>
                            {(has("model-costs:update") || has("model-costs:delete")) && (
                              <td className="act">
                                <div className="po-rowacts">
                                  {has("model-costs:update") && (
                                    <button type="button" className="po-ib" title="Düzenle" aria-label="Düzenle" onClick={() => handleEdit(item)}>{I.edit}</button>
                                  )}
                                  {has("model-costs:delete") && (
                                    <button type="button" className="po-ib danger" title="Kaldır" aria-label="Kaldır" onClick={() => handleDelete(item)}>{I.trash}</button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditModelCostModal
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={handleUpdateModelCost}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (orderId || addOrderId) && modelId && (
        <AddModelCostModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddModelCost}
          costItems={costItems}
          workshops={workshops}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Kaldır"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

// Edit Modal Component
interface EditModalProps {
  item: ModelCost;
  onClose: () => void;
  onSave: (item: ModelCost) => void;
}

const EditModelCostModal: React.FC<EditModalProps> = ({
  item,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<ModelCost>(item);
  useEscClose(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof ModelCost, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="mc-edit-title" onSubmit={handleSubmit}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Model maliyeti</span>
            <h3 id="mc-edit-title">Miktarı düzenle</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>
        <div className="po-modal-body">
          {/* Maliyet kalemi bilgisi — yalnız gösterim */}
          <dl className="po-facts">
            <div className="wide"><dt>Maliyet kalemi</dt><dd>{formData.costItemName || "—"}</dd></div>
            <div><dt>Kategori</dt><dd>{formData.costCategoryName || "—"}</dd></div>
            <div><dt>Birim fiyat</dt><dd>{formData.unitPrice} {formData.currency}</dd></div>
          </dl>

          <div className="po-grid2">
            <label className="po-field">
              <span>{formData.quantity1Label || "Miktar"} <em className="err">zorunlu</em></span>
              <input
                type="number"
                step="0.01"
                value={formData.quantity || ""}
                onChange={(e) => handleChange("quantity", parseFloat(e.target.value) || 0)}
                required
                autoFocus
              />
            </label>
            <label className="po-field">
              <span>Birim</span>
              <input type="text" value={formData.unit || ""} readOnly disabled />
            </label>
            <label className="po-field span2">
              <span>Miktar 1 etiketi <em>isteğe bağlı</em></span>
              <input
                type="text"
                value={formData.quantity1Label || ""}
                onChange={(e) => handleChange("quantity1Label", e.target.value || undefined)}
                placeholder="Örn: En, Boy, Adet"
              />
            </label>

            {/* Quantity 2 - Only if exists */}
            {formData.quantity2 !== undefined && formData.quantity2 !== null && (
              <>
                <label className="po-field">
                  <span>{formData.quantity2Label || "Miktar 2"}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity2 || ""}
                    onChange={(e) => handleChange("quantity2", parseFloat(e.target.value) || undefined)}
                  />
                </label>
                <label className="po-field">
                  <span>Birim 2</span>
                  <input type="text" value={formData.unit2 || ""} readOnly disabled />
                </label>
                <label className="po-field span2">
                  <span>Miktar 2 etiketi <em>isteğe bağlı</em></span>
                  <input
                    type="text"
                    value={formData.quantity2Label || ""}
                    onChange={(e) => handleChange("quantity2Label", e.target.value || undefined)}
                    placeholder="Örn: Çeşit sayısı, Renk sayısı"
                  />
                </label>
              </>
            )}

            {/* Quantity 3 - Only if exists */}
            {formData.quantity3 !== undefined && formData.quantity3 !== null && (
              <>
                <label className="po-field">
                  <span>Miktar 3 <em>referans</em></span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity3 || ""}
                    onChange={(e) => handleChange("quantity3", parseFloat(e.target.value) || undefined)}
                  />
                </label>
                <label className="po-field">
                  <span>Birim 3 <em>referans</em></span>
                  <input type="text" value={formData.unit3 || ""} readOnly disabled />
                </label>
              </>
            )}
          </div>
        </div>
        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={onClose}>Vazgeç</button>
          <button type="submit" className="po-btn primary">Kaydet</button>
        </div>
      </form>
    </div>
  );
};

// Add Modal Component
interface AddModalProps {
  onClose: () => void;
  onSave: (cost: Partial<ModelCost>) => void;
  costItems: CostItem[];
  workshops: Workshop[];
}

// Form verisi için özel tip - ModelCost'a ek olarak label alanları içeriyor
interface ModelCostFormData extends Partial<ModelCost> {
  quantity1Label?: string;
  quantity2Label?: string;
}

const AddModelCostModal: React.FC<AddModalProps> = ({
  onClose,
  onSave,
  costItems,
  workshops,
}) => {
  const [formData, setFormData] = useState<ModelCostFormData>({
    costItemId: "",
    quantity: 0,
    unit: "",
    quantity2: undefined,
    unit2: "",
    unitPrice: 0,
    totalCost: 0,
    currency: "TRY",
    usage: "",
    notes: "",
    wastagePercentage: 0,
    quantity1Label: "",
    quantity2Label: "",
  });

  const [selectedCostItem, setSelectedCostItem] = useState<CostItem | null>(
    null,
  );
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>("");
  const [workshopCostItems, setWorkshopCostItems] = useState<CostItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [formError, setFormError] = useState("");
  useEscClose(onClose);

  // Atölye seçildiğinde o atölyeye ait maliyet kalemlerini yükle
  const handleWorkshopChange = async (workshopId: string) => {
    setSelectedWorkshopId(workshopId);
    setFormError("");
    const selectedWorkshop = workshops.find((w) => w.workshopId === workshopId);

    setFormData((prev) => ({
      ...prev,
      usage: selectedWorkshop?.name || "",
      costItemId: "", // Maliyet kalemini sıfırla
    }));

    if (workshopId) {
      try {
        setLoadingItems(true);
        const workshopItems =
          await costService.getWorkshopCostItems(workshopId);

        // WorkshopCostItem'ları CostItem formatına dönüştür
        // API flat bir yapı döndürüyor, nested değil
        const items: CostItem[] = workshopItems
          .filter((wi) => wi.costItemId) // costItemId null olanları filtrele
          .map((wi) => ({
            costItemId: wi.costItemId, // Flat yapıda direkt costItemId
            costCategoryId: "", // API'de yok
            itemName: wi.costItemName || "", // costItemName
            description: wi.costItemDescription,
            unit: wi.unitCode,
            unitName: wi.quantity1Label, // quantity1Label birim adı gibi
            unit2: wi.unitCode2,
            unitPrice: wi.effectivePrice || wi.standardPrice || 0,
            currency: wi.effectiveCurrency || wi.standardCurrency || "TRY",
            supplier: undefined,
            wastagePercentage: undefined,
            minimumOrderQuantity: undefined,
            discountPercentage: wi.workshopDiscountPercentage || undefined,
            lastPriceUpdate: new Date().toISOString(),
            isActive: true,
            calculationType: wi.calculationType,
            materialWidth: wi.materialWidth,
            materialHeight: wi.materialHeight,
            quantity1Label: wi.quantity1Label,
            quantity2Label: wi.quantity2Label,
            quantity3Label: wi.quantity3Label,
            costCategory: {
              costCategoryId: "",
              category: wi.categoryName || "", // 'category' alanı zorunlu
              categoryName: wi.categoryName || "",
              description: undefined,
              displayOrder: undefined,
              isActive: true,
            },
          }));

        setWorkshopCostItems(items);
      } catch (error) {
        console.error("❌ Atölye maliyet kalemleri yüklenemedi:", error);
        setWorkshopCostItems([]);
      } finally {
        setLoadingItems(false);
      }
    } else {
      setWorkshopCostItems([]);
    }
  };

  const handleCostItemChange = (costItemId: string) => {
    const item = workshopCostItems.find((i) => i.costItemId === costItemId);
    setSelectedCostItem(item || null);
    setFormError("");

    setFormData((prev) => ({
      ...prev,
      costItemId,
      // Birim fiyat ve para birimi costItem'dan alınıyor, güncellenmiyor
      unitPrice: item?.unitPrice || 0,
      currency: item?.currency || "TRY",
      unit: item?.unitName || item?.unit || "",
      unit2: item?.unit2 || "",
      quantity1Label: item?.quantity1Label,
      quantity2Label: item?.quantity2Label,
    }));
  };

  const calculateTotal = () => {
    const qty1 = formData.quantity || 0;
    // Boya hesaplamada Çeşit Sayısı (quantity2) yalnız bilgi amaçlı — çarpana girmez
    const isPaintBased =
      selectedCostItem?.calculationType === CalculationType.PaintBased;
    const qty2 = isPaintBased ? 1 : formData.quantity2 || 1;
    const price = formData.unitPrice || 0;
    const wastage = formData.wastagePercentage || 0;

    let total = qty1 * qty2 * price;
    if (wastage > 0) {
      total = total * (1 + wastage / 100);
    }

    setFormData((prev) => ({ ...prev, totalCost: total }));
  };

  useEffect(() => {
    calculateTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.quantity,
    formData.quantity2,
    formData.unitPrice,
    formData.wastagePercentage,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedWorkshopId) {
      setFormError("Lütfen bir atölye seçin.");
      return;
    }

    if (!formData.costItemId) {
      setFormError("Lütfen bir maliyet kalemi seçin.");
      return;
    }

    if (!formData.quantity || formData.quantity <= 0) {
      setFormError("Lütfen geçerli bir miktar girin.");
      return;
    }

    // Label alanlarını ModelCost'a göndermeden önce çıkar
    const { quantity1Label, quantity2Label, ...modelCostData } = formData;
    onSave(modelCostData);
  };

  const itemPlaceholder = loadingItems
    ? "Yükleniyor..."
    : !selectedWorkshopId
      ? "Önce atölye seçin"
      : workshopCostItems.length === 0
        ? "Bu atölyede tanımlı maliyet yok"
        : "Seçin...";

  return (
    <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="mc-add-title" onSubmit={handleSubmit} noValidate>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Model maliyeti</span>
            <h3 id="mc-add-title">Yeni maliyet kalemi</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>
        <div className="po-modal-body">
          <div className="po-form-sec">
            <h4>Kaynak</h4>
            <div className="po-grid2">
              {/* Atölye - en üstte ve zorunlu */}
              <label className="po-field">
                <span>Atölye <em className="err">zorunlu</em></span>
                <select value={selectedWorkshopId} onChange={(e) => handleWorkshopChange(e.target.value)} required autoFocus>
                  <option value="">Önce atölye seçin...</option>
                  {workshops.map((workshop) => (
                    <option key={workshop.workshopId} value={workshop.workshopId}>
                      {workshop.name}
                    </option>
                  ))}
                </select>
              </label>

              {/* Maliyet kalemi - atölye seçilince aktif olur */}
              <label className="po-field">
                <span>Maliyet kalemi <em className="err">zorunlu</em></span>
                <select
                  value={formData.costItemId || ""}
                  onChange={(e) => handleCostItemChange(e.target.value)}
                  required
                  disabled={!selectedWorkshopId || loadingItems}
                >
                  <option value="">{itemPlaceholder}</option>
                  {workshopCostItems.map((item) => (
                    <option key={item.costItemId} value={item.costItemId}>
                      {item.itemName} - {item.costCategory?.categoryName} (
                      {formatCurrency(item.unitPrice, item.currency)})
                    </option>
                  ))}
                </select>
                {selectedWorkshopId && workshopCostItems.length === 0 && !loadingItems && (
                  <span className="po-fieldnote warn">Bu atölyede henüz maliyet kalemi tanımlanmamış.</span>
                )}
              </label>
            </div>
          </div>

          <div className="po-form-sec">
            <h4>Miktar</h4>
            <div className="po-grid2">
              <label className="po-field">
                <span>{formData.quantity1Label || "Miktar"} ({formData.unit || "birim"}) <em className="err">zorunlu</em></span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity || ""}
                  onChange={(e) => {
                    setFormError("");
                    setFormData((prev) => ({
                      ...prev,
                      quantity: parseFloat(e.target.value) || 0,
                    }));
                  }}
                  required
                  placeholder="Miktar girin"
                />
              </label>

              {/* Miktar 2 - Sadece quantity2Label varsa göster */}
              {formData.quantity2Label && (
                <label className="po-field">
                  <span>{formData.quantity2Label} {formData.unit2 && `(${formData.unit2})`}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity2 || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        quantity2: parseFloat(e.target.value) || undefined,
                      }))
                    }
                    placeholder="Miktar girin"
                  />
                </label>
              )}

              <label className="po-field span2">
                <span>Notlar <em>isteğe bağlı</em></span>
                <textarea
                  value={formData.notes || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="İsteğe bağlı notlar..."
                />
              </label>
            </div>
          </div>

          {formError && <div className="po-note err" role="alert">{formError}</div>}
        </div>
        <div className="po-modal-foot between">
          {/* Toplam tutar (otomatik hesaplanır) */}
          <div className="po-sum">
            <div className="grand">
              <span>Toplam tutar</span>
              <b>{formatCurrency(formData.totalCost || 0, formData.currency || "TRY")}</b>
            </div>
          </div>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose}>Vazgeç</button>
            <button type="submit" className="po-btn primary">Kaydet</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ModelCosts;
